import { useEffect, useState } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { Tooltip, Button, Modal, Divider, message } from "antd";
import { QuestionCircleOutlined, CloseOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { format, findDeep } from "@/utils";
import api from "@/api";
import { publicPath, getAiDevPlatformUrl } from "@/utils/base";

const { confirm } = Modal;
import dataJson from "@/../public/data.json";

const prefix = "myAiModelDetail-info";
import "./index.less";
import { useTypeKeyInfo } from "@/hooks/useTypeKeyInfo";

function Info() {
  const { my_ai_model_detail, confirmText, cancelText, release, delete_text } = dataJson;

  const [aiModelMapping, setAiModelMapping] = useState<any>();

  const navigate = useNavigate();
  const { typeKey, id } = useParams();
  const { isTampered = false } = useOutletContext<{ isTampered?: boolean }>() || {};

  const aiModelDetail = useSelector((state: any) => state.aiModelDetail);
  const appTypes = useSelector((state: any) => state.appTypes);

  const handleDelete = () => {
    confirm({
      title: delete_text + my_ai_model_detail?.detail_title,
      icon: null,
      width: 328,
      centered: true,
      closable: true,
      closeIcon: <CloseOutlined />,
      okText: confirmText,
      cancelText: cancelText,
      content: (
        <div className="delete-modal-content">
          {my_ai_model_detail.app_delete_content1}&quot;{aiModelMapping.name}&quot;
          {my_ai_model_detail.app_delete_content2}
        </div>
      ),
      onOk: async () => {
        try {
          const res: any = await api.deleteAiModel({ ids: [id] });

          if (res?.code == 200) {
            navigate(`${publicPath}/my_ai_model/${typeKey}`);
          } else {
            message.error(res.msg);
          }
        } catch (errorInfo: any) {
          console.error(errorInfo);
        }
      },
    });
  };

  useEffect(() => {
    if (aiModelDetail) {
      const firstTwo = aiModelDetail.histories.slice(0, 2);
      const isDel = firstTwo.every((item: any) => {
        const resShowMode = findDeep(release.show_mode_arr, item.showMode);
        return resShowMode?.baseBtns?.includes("delete");
      });
      const resDataCopy = {
        ...aiModelDetail,
        createTimeText: format(new Date(Number(aiModelDetail?.createTime)), "yyyy年MM月dd日 HH:mm:ss"),
        isDel,
        packageType: typeKey !== "AI_MODEL" ? aiModelDetail.subType.join("/") : "",
      };
      setAiModelMapping(resDataCopy);
      return;
    }
    setAiModelMapping(undefined);
  }, [aiModelDetail, appTypes]);

  /** AI_MODEL：下载工具；AGENT：打开 AI开发平台（新窗口，按环境跳转） */
  const handleDownload = (url: string) => {
    confirm({
      title: my_ai_model_detail.download_text + my_ai_model_detail.tool_download_text,
      icon: null,
      width: 328,
      centered: true,
      closable: true,
      closeIcon: <CloseOutlined />,
      okText: confirmText,
      cancelText: cancelText,
      onOk: () => {
        const link = document.createElement("a");
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
    });
  };

  const handleOpenAiDevPlatform = () => {
    window.open(getAiDevPlatformUrl(), "_blank", "noopener,noreferrer");
  };

  const handleToolLinkClick = () => {
    if (typeKey === "AI_MODEL") {
      handleDownload(my_ai_model_detail.tool_link);
    } else if (typeKey === "AGENT") {
      handleOpenAiDevPlatform();
    }
  };

  const { typeKeyInfo } = useTypeKeyInfo(typeKey, appTypes);

  return (
    <div className={prefix}>
      <div className={`${prefix}-inner`}>
        <div className={`${prefix}-header`}>
          <div className={`${prefix}-header-text`}>
            <div className={`${prefix}-header-title`}>{typeKeyInfo?.name}</div>
            <div className={`${prefix}-header-tip`}>
              {typeKeyInfo?.name}
              {my_ai_model_detail.info_tip}
            </div>
          </div>
          <div className={`${prefix}-header-btns`}>
            <Button
              className={`${prefix}-content-delete`}
              disabled={isTampered || !aiModelMapping?.isDel}
              onClick={handleDelete}
            >
              {delete_text + typeKeyInfo?.name}
            </Button>
          </div>
        </div>
        <div className={`${prefix}-content`}>
          <div className={`${prefix}-content-title`}>{my_ai_model_detail.developer_title}</div>
          <div className={`${prefix}-content-developer`}>
            <Tooltip
              placement="bottomLeft"
              title={my_ai_model_detail.developer_tip}
              overlayClassName={`${prefix}-content-developer-tooltip`}
              color="#fff"
              overlayInnerStyle={{
                color: "rgba(0,0,0,0.8)",
                width: 200,
                padding: "10px 16px",
                transform: "translate(-12px, 0)",
              }}
            >
              <QuestionCircleOutlined style={{ color: "#0A59F7", cursor: "pointer", fontSize: 14 }} />
            </Tooltip>
            <div className={`${prefix}-content-developer-id`}>
              <span>{my_ai_model_detail.developer_ID}</span>
              {aiModelMapping?.developerId}
            </div>
          </div>
          <Divider />
          <div className={`${prefix}-content-title mt-title`}>{typeKeyInfo?.name}</div>
          <div className={`${prefix}-content-value`}>
            <span>{my_ai_model_detail.package_type}</span>
            {aiModelMapping?.packageType || my_ai_model_detail.package_type_text}
          </div>
          {aiModelMapping?.packageName && (
            <div className={`${prefix}-content-value`}>
              <span>{my_ai_model_detail.package_name}</span>
              {aiModelMapping?.packageName}
            </div>
          )}
          <div className={`${prefix}-content-value`}>
            <span>
              {typeKeyInfo?.name}
              {my_ai_model_detail.al_model_ID}
            </span>
            {aiModelMapping?.currentId}
          </div>
          <div className={`${prefix}-content-value`}>
            <span>{my_ai_model_detail.create_time}</span>
            {aiModelMapping?.createTimeText}
          </div>

          <Divider />
          <div className={`${prefix}-content-title mt-title`}>{my_ai_model_detail.development_kit}</div>
          <div className={`${prefix}-content-value`}>
            <span>{my_ai_model_detail.tool}</span>
            <a onClick={handleToolLinkClick} style={{ cursor: "pointer" }}>
              {typeKey === "AI_MODEL"
                ? typeKeyInfo?.name + my_ai_model_detail.tool_download_text
                : my_ai_model_detail.tool_link_text}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Info;
